import { useState, useEffect, useCallback } from 'react';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Collapse,
  TextField,
  InputAdornment,
  CircularProgress,
  Link,
  Tooltip,
} from '@mui/material';

// Icons
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';

// API
import api from '../../../api/axios';

// Styles
import { table_container_base, table_header_with_search } from '../../../static/styles';

const ACTION_CONFIG = {
  create: { icon: AddCircleIcon, color: 'success', label: 'Created' },
  update: { icon: EditIcon, color: 'info', label: 'Updated' },
  delete: { icon: DeleteIcon, color: 'error', label: 'Deleted' },
  restore: { icon: RestoreIcon, color: 'warning', label: 'Restored' },
  status_change: { icon: SwapHorizIcon, color: 'primary', label: 'Status Changed' },
  clone: { icon: ContentCopyIcon, color: 'secondary', label: 'Cloned' },
};

const ENTITY_LINKS = {
  appointment: '/appointments',
  client: '/clients',
  property: '/properties',
  proposal: '/proposals',
  invoice: '/invoices',
  equipment: '/items',
  scope_of_work: '/items',
  warranty: '/items',
  guarantee: '/items',
};

const ChangesRow = ({ changes }) => {
  if (!changes || Object.keys(changes).length === 0) return null;

  return (
    <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        Changes:
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Old Value</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>New Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(changes).map(([field, { old: oldVal, new: newVal }]) => (
            <TableRow key={field}>
              <TableCell sx={{ color: 'text.secondary' }}>
                {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </TableCell>
              <TableCell sx={{ color: 'error.main', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {formatValue(oldVal)}
              </TableCell>
              <TableCell sx={{ color: 'success.main', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {formatValue(newVal)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    if (value.name) return value.name;
    return JSON.stringify(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const LogRow = ({ log }) => {
  const [open, setOpen] = useState(false);
  const hasChanges = log.changes && Object.keys(log.changes).length > 0;
  const actionConfig = ACTION_CONFIG[log.action] || ACTION_CONFIG.update;
  const ActionIcon = actionConfig.icon;
  const entityLink = ENTITY_LINKS[log.entityType];

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: hasChanges ? 'unset' : undefined } }}>
        <TableCell width="5%">
          {hasChanges && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell width="15%">
          {moment(log.createdAt).format('MM/DD/YYYY HH:mm')}
        </TableCell>
        <TableCell width="12%">
          <Chip
            icon={<ActionIcon sx={{ fontSize: 16 }} />}
            label={actionConfig.label}
            color={actionConfig.color}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell width="20%">
          {entityLink && log.entityId ? (
            <Link
              href={`${entityLink}/${log.entityId}`}
              color="primary"
              underline="hover"
              sx={{ fontWeight: 500 }}
            >
              {log.entityName || log.entityId.substring(0, 8)}
            </Link>
          ) : (
            <Typography variant="body2">
              {log.entityName || log.entityId?.substring(0, 8) || '—'}
            </Typography>
          )}
        </TableCell>
        <TableCell width="18%">
          {log.user ? (
            <Tooltip title={log.user.email || ''}>
              <Typography variant="body2">
                {log.user.name || log.user.email || 'Unknown'}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              System
            </Typography>
          )}
        </TableCell>
        <TableCell width="30%">
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {Object.entries(log.metadata).slice(0, 3).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${formatValue(value)}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          )}
        </TableCell>
      </TableRow>
      {hasChanges && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <ChangesRow changes={log.changes} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ActivityLogsTable = ({ entityType, title }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      
      if (search) {
        params.search = search;
      }

      let response;
      if (entityType) {
        response = await api.get(`/activity-logs/entity/${entityType}`, { params });
      } else {
        response = await api.get('/activity-logs', { params });
      }

      setLogs(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [entityType, page, rowsPerPage, search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimeout) clearTimeout(searchTimeout);
    
    setSearchTimeout(setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 500));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={table_header_with_search}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D3B59' }}>
          {title || 'Activity Logs'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />
          <IconButton onClick={fetchLogs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={table_container_base}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell width="5%"></TableCell>
              <TableCell width="15%">Date & Time</TableCell>
              <TableCell width="12%">Action</TableCell>
              <TableCell width="20%">Entity</TableCell>
              <TableCell width="18%">User</TableCell>
              <TableCell width="30%">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No activity logs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => <LogRow key={log.id} log={log} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

export default ActivityLogsTable;
